import {
  Check,
  ChevronsUpDown,
  Plus,
  Settings,
  Wallet,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import {
  useAuth,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import type { OrganizationMembershipResource } from "@clerk/shared/types";
import { useDashboardStore } from "@/stores";
import { useAdminRole } from "@/hooks/IsAdminContext";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CreateTeamDialog } from "./create-team-dialog";
import { Avatar, AvatarImage } from "@/components/ui/avatar";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export function TeamSwitcher() {
  const { isMobile } = useSidebar();
  const { organization } = useOrganization();
  const { isAdmin } = useAdminRole();
  const [loadingWallet, setLoadingWallet] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { usage } = useDashboardStore();
  const { setActive, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 100, initialPage: 1 },
  });

  const { user } = useUser();
  const [createTeamOpen, setCreateTeamOpen] = React.useState(false);
  const { resetStore } = useDashboardStore();

  const setActiveTeam = async (team: OrganizationMembershipResource) => {
    await setActive?.({ organization: team.organization.id });
    resetStore();
    navigate(`/dashboard/`);
  };

  const filteredMemberships = React.useMemo(() => {
    const otherOrgs = userMemberships.data?.filter(
      (mem) => mem.organization.id !== organization?.id
    );
    if (!searchQuery.trim()) return otherOrgs;
    return otherOrgs?.filter((mem) =>
      mem.organization.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [userMemberships.data, organization?.id, searchQuery]);

  const fetchPortalSession = async () => {
    if (loadingWallet) return; // Prevent multiple clicks
    setLoadingWallet(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/subscriptions/createPortaslSession`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch plans");
      }
      const { data } = await response.json();
      const url = data?.url;
      // open url in the same tab
      if (url) {
        window.location = url;
      } else {
        setLoadingWallet(false);
        throw new Error("No URL found in response");
      }
    } catch (error) {
      setLoadingWallet(false);
      console.error("Error:", error);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu onOpenChange={() => setSearchQuery("")}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <img
                  src={organization?.imageUrl}
                  alt="Org Avatar"
                  className="size-8 rounded-md border p-0.25 bg-muted-foreground/10 shrink-0"
                />
              </div>
              <div className="flex flex-col items-start justify-center truncate">
                <span className="text-sm font-medium">
                  {organization?.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {organization?.membersCount} members
                </span>
              </div>
              <ChevronsUpDown className="ml-auto opacity-60" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-64 w-auto p-1 rounded-xl overflow-auto max-h-150 [scrollbar-color:--alpha(var(--foreground)/20%)_transparent] scrollbar-thin"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <div className="flex flex-row items-center gap-2 p-2">
              <div className="text-sidebar-primary-foreground flex aspect-square size-11 items-center justify-center rounded-lg">
                <img
                  src={organization?.imageUrl}
                  alt="Org Avatar"
                  className="size-10 rounded-lg border p-0.25 bg-muted-foreground/10 shrink-0"
                />
              </div>
              <div className="flex flex-col items-start justify-center truncate">
                <span className="text-sm font-medium">
                  {organization?.name}
                </span>
                <span className="text-xs text-muted-foreground font-normal">
                  {organization?.membersCount} members
                </span>
              </div>
            </div>

            <div className="flex flex-row items-center gap-2 px-2">
              <DropdownMenuItem className="p-0 m-0 border-0">
                <Button
                  disabled={loadingWallet}
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate("/dashboard/settings/organization")
                  }
                >
                  <Settings /> Settings
                </Button>
              </DropdownMenuItem>
              {isAdmin &&
                !usage?.plan.toLowerCase().includes("free") &&
                !usage?.plan.toLowerCase().includes("unlimited") && (
                  <Button
                    onClick={fetchPortalSession}
                    disabled={loadingWallet}
                    variant="secondary"
                    size="sm"
                  >
                    {loadingWallet && <Spinner />}
                    {!loadingWallet && <Wallet className="opacity-60" />}
                    Subscription
                  </Button>
                )}
            </div>

            <DropdownMenuLabel className="text-muted-foreground text-xs flex flex-row justify-between mt-4">
              <span className="mr-auto">Teams</span>
              {userMemberships.data?.length}
            </DropdownMenuLabel>

            {filteredMemberships?.map((mem) => (
              <DropdownMenuItem
                key={mem?.id}
                onClick={() => setActiveTeam(mem as OrganizationMembershipResource)}
                className="gap-2 p-1.5 rounded-lg"
              >
                <div className="flex size-9 items-center justify-center rounded-md border">
                  <img
                    src={mem.organization.imageUrl}
                    className="size-7.5 rounded-sm"
                  />
                </div>
                <div className="text-sm font-medium truncate flex flex-col">
                  <span>{mem?.organization?.name}</span>
                  <span className="text-muted-foreground text-xs font-normal">
                    {mem?.organization.membersCount} members
                  </span>
                </div>
                <DropdownMenuShortcut className="text-xs text-muted-foreground">
                  {mem?.organization.id === organization?.id ? <Check /> : ""}
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            {searchQuery && filteredMemberships?.length === 0 && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No teams found
              </div>
            )}
            {/* The dialog is a sibling of the menu, opened from onSelect, so
                it survives the menu closing. */}
            {user?.createOrganizationEnabled !== false && (
              <DropdownMenuItem
                onSelect={() => setCreateTeamOpen(true)}
                className="gap-2 p-1.5 rounded-lg text-muted-foreground"
              >
                <div className="flex size-9 items-center justify-center rounded-md border border-dashed bg-sidebar">
                  <Plus className="size-4" />
                </div>
                <span className="text-sm font-medium truncate">
                  Create Team
                </span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <CreateTeamDialog open={createTeamOpen} onOpenChange={setCreateTeamOpen} />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function TeamSwitcherStandalone() {
  const { organization } = useOrganization();
  const { setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const setActiveTeam = async (team: OrganizationMembershipResource) => {
    await setActive?.({ organization: team.organization.id });
    window.location.href = `/dashboard/`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          className="justify-start gap-2 h-auto p-2 data-[state=open]:bg-accent w-full"
        >
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
            <img
              src={organization?.imageUrl}
              alt="Org Avatar"
              className="size-8 rounded-md border p-0.25 bg-muted-foreground/10 shrink-0"
            />
          </div>
          <div className="flex flex-col items-start justify-center truncate">
            <span className="text-sm font-medium">{organization?.name}</span>
            <span className="text-xs text-muted-foreground">
              {organization?.membersCount} members
            </span>
          </div>
          <ChevronsUpDown className="ml-auto opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-64"
        align="start"
      >
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          Teams
        </DropdownMenuLabel>
        {userMemberships.data?.map((mem) => (
          <DropdownMenuItem
            key={mem?.id}
            onClick={() => setActiveTeam(mem as OrganizationMembershipResource)}
            className="gap-2 p-1.5"
          >
            <Avatar variant="square">
              <AvatarImage src={mem.organization.imageUrl} alt={mem.organization.name} />
            </Avatar>
            <div className="text-sm font-medium truncate flex flex-col">
              <span>{mem?.organization?.name}</span>
              <span className="text-muted-foreground text-xs">
                {mem?.organization.membersCount} members
              </span>
            </div>
            <DropdownMenuShortcut className="text-xs text-muted-foreground">
              {mem?.organization.id === organization?.id ? <Check /> : ""}
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
